import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { paths } from 'src/routes/paths';
import { useCouponsStore } from 'src/store/benefits/coupons.store';
import { useCompaniesStore } from 'src/store/companies/companies.store';
import { useLpEventsStore } from 'src/store/benefits/lp-events.store';
import { useProductsStore } from 'src/store/products/products.store';
import { usePartnersStore } from 'src/store/partners/partners.store';
import { useRedeemsStore } from 'src/store/redeems/redeems.store';
import { convertFile } from 'src/utils/convert-file.utils';
import * as XLSX from 'xlsx';
import useNavigate from 'src/hooks/use-navigate';

export type BenefitDetailItem = {
  value: string;
};

export type BenefitOptionItem = {
  value: string;
  label: string;
  image?: string | null;
} | null;

export type BenefitDetailsFormValues = {
  // Exibição
  benefitType: 'coupon' | 'product';
  status: 'active' | 'inactive';
  exhibitionEnv: 'viva' | 'lp_events' | 'whatsapp' | 'reward' | 'parking';
  businessUnitId: string;
  lpEvent: string;
  benefitForNewUsers: boolean;
  newUsersPeriod: {
    startDate: Date;
    endDate: Date;
  };
  isGift: boolean;
  blocking: 'all' | 'self';
  test: boolean;
  bonusProduct: boolean;
  exclusiveToBiome: boolean;
  private: boolean;
  dontShowToImported: boolean;
  highlightCategory: string[];
  localType: 'company' | 'local';
  company: BenefitOptionItem;
  local: BenefitOptionItem;
  showTo: string[];
  show: string[];
  tiers: string[];
  priority: number;

  // Informações
  name: string;
  price: number;
  discountPercentage: number;
  startDate: Date;
  endDate: Date;
  image: string | (File & { preview?: string }) | null;
  description: string;
  details: BenefitDetailItem[];
  secondaryDescription: string;
  category: string;
  theme: string;
  rules: string;

  // Resgate
  rewardType: 'code' | 'giftback' | 'simple' | 'uniqueCode';
  codeShowType: 'code' | 'link';
  redeemCodeQuantity: number;
  expirationDays: number;
  userPurchaseLimit: number;
  uniqueCode: string;
  hasQuantity: boolean;
  totalQuantity: number;
  onlyToRedeemedPoints: boolean;
  location: string;
  hasCustomerVoice: boolean;
  customerVoiceData: {
    baseUrl: string;
    assetsUrl: string;
    customerVoiceFormId: string;
  };
};

const detailsSchema: any = Yup.array().when(['benefitType', 'exhibitionEnv'], {
  is: (benefitType: string, exhibitionEnv: string) =>
    benefitType === 'product' && exhibitionEnv === 'viva',
  then: (schema: any) =>
    schema
      .of(
        Yup.object({
          value: Yup.string().required('Informe o valor'),
        })
      )
      .min(1, 'Adicione ao menos um detalhe')
      .required('Adicione os detalhes do produto'),
  otherwise: (schema: any) => schema.notRequired(),
});

const schema = Yup.object({
  benefitType: Yup.string().oneOf(['coupon', 'product']).required(),
  name: Yup.string().required('Informe o nome do benefício'),
  price: Yup.number().typeError('Informe um valor válido').min(0).required(),
  startDate: Yup.date().required('Informe a data de início'),
  endDate: Yup.date()
    .required('Informe a data de fim')
    .min(Yup.ref('startDate'), 'A data de fim do benefício não pode ser inferior à data de início'),
  image: Yup.mixed<NonNullable<BenefitDetailsFormValues['image']>>()
    .nullable()
    .when('exhibitionEnv', {
      is: 'parking',
      then: (imageSchema) => imageSchema.notRequired(),
      otherwise: (imageSchema) => imageSchema.required('Informe a imagem'),
    }),
  status: Yup.string().oneOf(['active', 'inactive']).required(),
  description: Yup.string().when('exhibitionEnv', {
    is: (exhibitionEnv: string) => exhibitionEnv !== 'whatsapp' && exhibitionEnv !== 'parking',
    then: (descriptionSchema) => descriptionSchema.required('Informe a descrição'),
    otherwise: (descriptionSchema) => descriptionSchema.notRequired(),
  }),
  secondaryDescription: Yup.string().when(['benefitType', 'exhibitionEnv'], {
    is: (benefitType: string, exhibitionEnv: string) =>
      benefitType === 'coupon' && exhibitionEnv === 'viva',
    then: (couponSchema) => couponSchema.required('Informe a descrição secundária'),
    otherwise: (productSchema) => productSchema.notRequired(),
  }),
  category: Yup.string(),
  theme: Yup.string(),
  details: detailsSchema,
  rules: Yup.string().required('Informe as regras'),
  exhibitionEnv: Yup.string()
    .oneOf(['viva', 'lp_events', 'whatsapp', 'reward', 'parking'])
    .required(),
  benefitForNewUsers: Yup.boolean().required(),
  blocking: Yup.string().oneOf(['all', 'self']).required(),
  private: Yup.boolean().required(),
  test: Yup.boolean().required(),
  exclusiveToBiome: Yup.boolean().required(),
  highlightCategory: Yup.array()
    .of(Yup.string().required())
    .when('exhibitionEnv', {
      is: 'lp_events',
      then: (highlightSchema) =>
        highlightSchema
          .test(
            'is-not-empty',
            'Selecione pelo menos uma opção',
            (value) => Array.isArray(value) && value.length > 0
          )
          .required(),
      otherwise: (highlightSchema) => highlightSchema.notRequired(),
    })
    .default([]),
  isGift: Yup.boolean().required(),
  bonusProduct: Yup.boolean().required(),
  localType: Yup.string().when(['benefitType', 'exhibitionEnv'], {
    is: (benefitType: string, exhibitionEnv: string) =>
      exhibitionEnv === 'reward' || (benefitType === 'product' && exhibitionEnv !== 'whatsapp'),
    then: (localTypeSchema) => localTypeSchema.oneOf(['company', 'local']).required(),
    otherwise: (localTypeSchema) => localTypeSchema.notRequired(),
  }),
  company: Yup.mixed<{ value: string; label: string }>()
    .nullable()
    .when(['benefitType', 'localType', 'exhibitionEnv'], {
      is: (benefitType: string, localType: string, exhibitionEnv: string) =>
        (exhibitionEnv === 'reward' ||
          (benefitType === 'product' && exhibitionEnv !== 'whatsapp')) &&
        localType === 'company',
      then: (companySchema) => companySchema.required('Informe a empresa'),
      otherwise: (companySchema) => companySchema.notRequired(),
    }),
  local: Yup.mixed<{ value: string; label: string }>()
    .nullable()
    .when(['benefitType', 'localType', 'exhibitionEnv'], {
      is: (benefitType: string, localType: string, exhibitionEnv: string) =>
        (exhibitionEnv === 'reward' ||
          (benefitType === 'product' && exhibitionEnv !== 'whatsapp')) &&
        localType === 'local',
      then: (localSchema) => localSchema.required('Informe o local'),
      otherwise: (localSchema) => localSchema.notRequired(),
    }),
  showTo: Yup.array().when(['benefitType', 'exhibitionEnv'], {
    is: (benefitType: string, exhibitionEnv: string) =>
      benefitType === 'coupon' && exhibitionEnv === 'viva',
    then: (showToSchema) =>
      showToSchema.test(
        'is-not-empty',
        'Selecione pelo menos uma opção',
        (value) => Array.isArray(value) && value.length > 0
      ),
    otherwise: (showToSchema) => showToSchema.notRequired(),
  }),
  show: Yup.array().when(['benefitType', 'exhibitionEnv'], {
    is: (benefitType: string, exhibitionEnv: string) =>
      benefitType === 'product' && exhibitionEnv === 'viva',
    then: (showSchema) =>
      showSchema.test(
        'is-not-empty',
        'Selecione pelo menos uma opção',
        (value) => Array.isArray(value) && value.length > 0
      ),
    otherwise: (showSchema) => showSchema.notRequired(),
  }),
  dontShowToImported: Yup.boolean().required(),
  rewardType: Yup.string().when(
    ['benefitType', 'exhibitionEnv'],
    ([benefitType, exhibitionEnv], rewardTypeSchema) => {
      if (exhibitionEnv === 'parking') {
        return rewardTypeSchema.notRequired();
      }

      if (benefitType === 'coupon') {
        return rewardTypeSchema.oneOf(['code', 'giftback']).required();
      }

      return exhibitionEnv === 'whatsapp'
        ? rewardTypeSchema.oneOf(['simple', 'uniqueCode']).required()
        : rewardTypeSchema.oneOf(['simple']).required();
    }
  ),
  codeShowType: Yup.string().when(['benefitType', 'rewardType', 'exhibitionEnv'], {
    is: (benefitType: string, rewardType: string, exhibitionEnv: string) =>
      exhibitionEnv !== 'parking' && (benefitType === 'coupon' || rewardType === 'code'),
    then: (codeSchema) => codeSchema.oneOf(['code', 'link']).required('Informe o tipo de exibição'),
    otherwise: (otherSchema) => otherSchema.notRequired(),
  }),
  redeemCodeQuantity: Yup.number()
    .typeError('Informe uma quantidade válida')
    .integer('A quantidade deve ser inteira')
    .when(['benefitType', 'rewardType', 'exhibitionEnv'], {
      is: (benefitType: string, rewardType: string, exhibitionEnv: string) =>
        exhibitionEnv !== 'parking' && (benefitType === 'coupon' || rewardType === 'code'),
      then: (codeSchema) => codeSchema.min(1, 'Informe no mínimo 1').required('Campo obrigatório'),
      otherwise: (otherSchema) => otherSchema.notRequired(),
    }),
  totalQuantity: Yup.number()
    .typeError('Informe uma quantidade válida')
    .integer('A quantidade deve ser inteira')
    .min(0, 'Não pode ser negativo')
    .when(['benefitType', 'rewardType'], {
      is: (benefitType: string, rewardType: string) =>
        benefitType === 'product' && rewardType === 'simple',
      then: (quantitySchema) => quantitySchema.required('Campo obrigatório'),
      otherwise: (quantitySchema) => quantitySchema.notRequired(),
    }),
  expirationDays: Yup.number()
    .typeError('Informe um valor válido')
    .integer('Informe um valor inteiro')
    .when('exhibitionEnv', {
      is: 'reward',
      then: (expirationSchema) =>
        expirationSchema.min(1, 'Informe no mínimo 1 dia').required('Campo obrigatório'),
      otherwise: (expirationSchema) =>
        expirationSchema.when('exhibitionEnv', {
          is: (exhibitionEnv: string) =>
            exhibitionEnv === 'whatsapp' || exhibitionEnv === 'parking',
          then: (whatsAppExpirationSchema) =>
            whatsAppExpirationSchema.min(0, 'Informe no mínimo 0').notRequired(),
          otherwise: (defaultExpirationSchema) =>
            defaultExpirationSchema.min(1, 'Informe no mínimo 1 dia').required('Campo obrigatório'),
        }),
    }),
  userPurchaseLimit: Yup.number()
    .typeError('Informe um valor válido')
    .integer('Informe um valor inteiro')
    .when('exhibitionEnv', {
      is: (exhibitionEnv: string) => exhibitionEnv === 'reward' || exhibitionEnv === 'parking',
      then: (limitSchema) => limitSchema.notRequired(),
      otherwise: (expirationSchema) =>
        expirationSchema.min(1, 'Informe no mínimo 1').required('Campo obrigatório'),
    }),
  priority: Yup.number()
    .typeError('Informe um valor válido')
    .integer('Informe um valor inteiro')
    .min(0, 'Não pode ser negativo')
    .required('Campo obrigatório'),
  tiers: Yup.array().when('exhibitionEnv', {
    is: 'parking',
    then: (tiersSchema) =>
      tiersSchema
        .length(1, 'Selecione um tier')
        .of(Yup.string().oneOf(['silver', 'gold', 'diamond']))
        .required('Selecione um tier'),
    otherwise: (tiersSchema) => tiersSchema.notRequired(),
  }),
  discountPercentage: Yup.number()
    .typeError('Informe uma porcentagem válida')
    .min(0, 'Não pode ser negativo')
    .max(100, 'A porcentagem não pode ser maior que 100')
    .when('exhibitionEnv', {
      is: 'parking',
      then: (discountSchema) => discountSchema.required('Informe a porcentagem de desconto'),
      otherwise: (discountSchema) => discountSchema.notRequired(),
    }),
  location: Yup.string().when(['benefitType', 'exhibitionEnv'], {
    is: (benefitType: string, exhibitionEnv: string) =>
      benefitType === 'product' && exhibitionEnv !== 'whatsapp',
    then: (pickupSchema) => pickupSchema.required('Informe o local de retirada'),
    otherwise: (pickupSchema) => pickupSchema.notRequired(),
  }),
  businessUnitId: Yup.string().when('exhibitionEnv', {
    is: 'lp_events',
    then: (businessUnitSchema) => businessUnitSchema.required('Unidade de Negócio é obrigatória'),
    otherwise: (businessUnitSchema) => businessUnitSchema.notRequired(),
  }),
  lpEvent: Yup.string().when('exhibitionEnv', {
    is: 'lp_events',
    then: (eventSchema) => eventSchema.required('Evento LP é obrigatório'),
    otherwise: (eventSchema) => eventSchema.notRequired(),
  }),
  hasCustomerVoice: Yup.boolean().required(),
  customerVoiceData: Yup.object({
    baseUrl: Yup.string(),
    assetsUrl: Yup.string(),
    customerVoiceFormId: Yup.string(),
  })
    .when(['hasCustomerVoice', 'exhibitionEnv'], {
      is: (hasCustomerVoice: boolean, exhibitionEnv: string) =>
        hasCustomerVoice && ['viva', 'lp_events', 'reward'].includes(exhibitionEnv),
      then: (customerVoiceSchema) =>
        customerVoiceSchema.shape({
          baseUrl: Yup.string().required('Campo obrigatório'),
          assetsUrl: Yup.string().required('Campo obrigatório'),
          customerVoiceFormId: Yup.string().required('Campo obrigatório'),
        }),
      otherwise: (customerVoiceSchema) =>
        customerVoiceSchema.shape({
          baseUrl: Yup.string().notRequired(),
          assetsUrl: Yup.string().notRequired(),
          customerVoiceFormId: Yup.string().notRequired(),
        }),
    })
    .required(),
}) as Yup.ObjectSchema<BenefitDetailsFormValues>;

export const EXHIBITION_ENV_OPTIONS = [
  { value: 'viva', label: 'VIVA CENTER NORTE' },
  { value: 'lp_events', label: 'EVENTOS LP' },
  { value: 'whatsapp', label: 'WHATSAPP' },
  { value: 'reward', label: 'TRILHA' },
  // Estacionamento oculto no cadastro; manter as regras para os registros existentes.
  // { value: 'parking', label: 'ESTACIONAMENTO' },
];

export const BOOLEAN_OPTIONS = [true, false] as const;
export const BOOLEAN_LABELS = ['Sim', 'Não'];

export const PRIVATE_OPTIONS = [true, false] as const;
export const PRIVATE_LABELS = ['Privado', 'Público'];

export const DONT_OPTIONS = [true, false] as const;
export const DONT_LABELS = ['Importados', 'Não importados'];

export const BLOCK_REDEEM_OPTIONS = ['all', 'self'] as const;
export const BLOCK_REDEEM_LABELS = ['Sim', 'Não'];

export const TIER_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'silver', label: 'Prata' },
  { value: 'gold', label: 'Ouro' },
  { value: 'diamond', label: 'Diamante' },
];

export const BENEFIT_TYPE_OPTIONS = ['product', 'coupon'] as const;
export const BENEFIT_TYPE_LABELS = ['Produto', 'Cupom'];
export const LOCAL_TYPE_OPTIONS = ['company', 'local'] as const;
export const LOCAL_TYPE_LABELS = ['Empresa', 'Local'];
export const COUPON_REWARD_OPTIONS = ['code', 'giftback'] as const;
export const COUPON_REWARD_LABELS = ['Código/Link', 'Giftback'];
export const PRODUCT_REWARD_OPTIONS = ['simple', 'code'] as const;
export const PRODUCT_REWARD_LABELS = ['Item', 'Código/Link'];
export const PRODUCT_WPP_REWARD_OPTIONS = ['simple', 'uniqueCode'] as const;
export const PRODUCT_WPP_REWARD_LABELS = ['Item', 'Código Único'];
export const CODE_SHOW_OPTIONS = ['code', 'link'] as const;
export const CODE_SHOW_LABELS = ['Campo para copiar', 'Botão de redirecionamento'];

export const useBenefitsDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const copyId = searchParams.get('copyId');
  const benefitId = id || copyId;
  const benefitTypeParam = searchParams.get('benefitType');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');
  const isCopyMode = !isEdit && Boolean(copyId);
  const shouldLoadBenefit = isEdit || Boolean(benefitId && benefitTypeParam);
  const { enqueueSnackbar } = useSnackbar();
  const {
    categories,
    themes,
    locations,
    getCategories,
    getThemes,
    getLocations,
    createCoupon,
    updateCoupon,
    getCouponDetails,
  } = useCouponsStore();
  const [createProduct, updateProduct, findProductById] = useProductsStore((state) => [
    state.create,
    state.update,
    state.findById,
  ]);
  const [findPartners, uploadImage] = usePartnersStore((state) => [
    state.findPartners,
    state.getUrlFile,
  ]);
  const [findRedeems] = useRedeemsStore((state) => [state.findRedeems]);
  const { companies, fetchCompanies } = useCompaniesStore();
  const { businessUnits, lpEventsByBusinessUnit, getBusinessUnits, getLpEventsByBusinessUnit } =
    useLpEventsStore();
  const [openCpfs, setOpenCpfs] = useState(false);
  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [codesEdited, setCodesEdited] = useState(false);
  const [fileNameCpfs, setFileNameCpfs] = useState('');
  const [cpfs, setCpfs] = useState<any[]>([]);
  const [openRedeemModal, setOpenRedeemModal] = useState(false);
  const [currentRedeem, setCurrentRedeem] = useState<any>(null);
  const [part, setPart] = useState<{ value: string; label: string }[]>([]);
  const [allredeems, setAllRedeems] = useState<any[]>([]);
  const [showToLocations, setShowToLocations] = useState<any[]>([]);
  const [isDetailsLoading, setIsDetailsLoading] = useState(shouldLoadBenefit);

  const title = isEdit ? 'Editar Benefício' : 'Criar Benefício';

  const defaultValues = useMemo<BenefitDetailsFormValues>(
    () => ({
      // Exibição
      benefitType: 'coupon',
      status: 'active',
      exhibitionEnv: 'viva',
      businessUnitId: '',
      lpEvent: '',
      benefitForNewUsers: false,
      newUsersPeriod: {
        startDate: new Date(),
        endDate: new Date(),
      },
      isGift: false,
      blocking: 'self',
      test: false,
      bonusProduct: false,
      exclusiveToBiome: false,
      private: false,
      dontShowToImported: false,
      highlightCategory: [],
      localType: 'company',
      company: null,
      local: null,
      showTo: [],
      show: [],
      tiers: ['all'],
      priority: 0,

      // Informações
      name: '',
      price: 0,
      discountPercentage: 0,
      startDate: new Date(),
      endDate: new Date(),
      image: null,
      description: '',
      details: [{ value: '' }],
      secondaryDescription: '',
      category: '',
      theme: '',
      rules: '',

      // Resgate
      rewardType: 'code', // deve ter apenas em cupons e produtos do tipo whatsapp
      codeShowType: 'code',
      redeemCodeQuantity: 1,
      expirationDays: 0,
      userPurchaseLimit: 1,
      uniqueCode: '',
      hasQuantity: false,
      totalQuantity: 0,
      onlyToRedeemedPoints: false,
      location: '',
      hasCustomerVoice: false,
      customerVoiceData: {
        baseUrl: '',
        assetsUrl: '',
        customerVoiceFormId: '',
      },
    }),
    []
  );

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        await Promise.all([
          getCategories(),
          getThemes(),
          getLocations(),
          fetchCompanies(),
          getBusinessUnits(),
        ]);

        const partners = await findPartners(true);
        if (!partners) {
          setPart([]);
        } else {
          setPart(partners.map((partner: any) => ({ value: partner._id, label: partner.name })));
        }

        const redeems = await findRedeems(true);
        if (!redeems) {
          setAllRedeems([]);
          return;
        }

        setAllRedeems(
          redeems.map((redeem: any) => {
            const normalizedRedeem = { ...redeem };

            if ('extra' in normalizedRedeem) {
              delete normalizedRedeem.extra;
            }

            return {
              value: normalizedRedeem._id,
              label: normalizedRedeem.details?.name || normalizedRedeem.name,
              partnerId: normalizedRedeem.partner,
              ...normalizedRedeem,
            };
          })
        );
      } catch (error) {
        console.error('Erro ao carregar filtros de benefício', error);
      }
    };

    fetchFilters();
  }, [
    fetchCompanies,
    findPartners,
    findRedeems,
    getBusinessUnits,
    getCategories,
    getLocations,
    getThemes,
  ]);

  const methods = useForm<BenefitDetailsFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onChange',
  });

  const { control, handleSubmit, watch, formState, setValue } = methods;
  const businessUnitId = watch('businessUnitId');
  const benefitType = watch('benefitType');
  const exhibitionEnv = watch('exhibitionEnv');
  const highlightCategory = watch('highlightCategory');

  useEffect(() => {
    if (benefitType !== 'product' || exhibitionEnv !== 'parking') {
      return;
    }

    setValue('exhibitionEnv', 'viva', {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [benefitType, exhibitionEnv, setValue]);

  useEffect(() => {
    if (exhibitionEnv === 'viva') {
      return;
    }

    if (currentRedeem) {
      setCurrentRedeem(null);
    }

    if (openRedeemModal) {
      setOpenRedeemModal(false);
    }
  }, [currentRedeem, exhibitionEnv, openRedeemModal]);

  useEffect(() => {
    if (exhibitionEnv !== 'whatsapp') {
      return;
    }

    setValue('businessUnitId', '');
    setValue('lpEvent', '');

    setValue('highlightCategory', []);
    setValue('expirationDays', 30);
    setValue('localType', 'local');

    if (Array.isArray(locations) && locations.length > 0) {
      const localToSet = locations.length > 1 ? locations[1] : locations[0];
      setValue('local', {
        value: localToSet.value,
        label: localToSet.label,
      });
    }
  }, [exhibitionEnv, locations, setValue]);

  useEffect(() => {
    if (exhibitionEnv !== 'reward') {
      return;
    }

    setValue('businessUnitId', '');
    setValue('lpEvent', '');
    setValue('benefitForNewUsers', false);
    setValue('blocking', 'self');
    setValue('test', false);
    setValue('bonusProduct', false);
    setValue('exclusiveToBiome', false);
    setValue('private', false);
    setValue('dontShowToImported', false);
    setValue('highlightCategory', []);
    setValue('showTo', []);
    setValue('show', []);
    setValue('tiers', ['all']);
    setValue('priority', 0);
    setValue('startDate', new Date());
    setValue('endDate', new Date());
    setValue('theme', '');
    setValue('userPurchaseLimit', 1);
  }, [exhibitionEnv, setValue]);

  useEffect(() => {
    if (exhibitionEnv !== 'reward' || benefitType !== 'coupon') {
      return;
    }

    setValue('price', 0, { shouldValidate: true });
  }, [benefitType, exhibitionEnv, setValue]);

  useEffect(() => {
    if (!businessUnitId) {
      setValue('lpEvent', '');
      return;
    }

    getLpEventsByBusinessUnit(businessUnitId);
  }, [businessUnitId, getLpEventsByBusinessUnit, setValue]);

  useEffect(() => {
    const currentHighlights = Array.isArray(highlightCategory) ? highlightCategory : [];

    if (exhibitionEnv !== 'lp_events') {
      if (currentHighlights.length > 0) {
        setValue('highlightCategory', [], { shouldValidate: true });
      }

      return;
    }

    let normalizedHighlights = currentHighlights.filter(
      (item) => item === 'lp_events_home_highlights' || item === 'without'
    );

    if (normalizedHighlights.length === 0) {
      normalizedHighlights = ['without'];
    }

    if (normalizedHighlights.includes('without') && normalizedHighlights.length > 1) {
      normalizedHighlights = ['without'];
    }

    const isSameLength = normalizedHighlights.length === currentHighlights.length;
    const isSameValues =
      isSameLength &&
      normalizedHighlights.every((value, index) => value === currentHighlights[index]);

    if (!isSameValues) {
      setValue('highlightCategory', normalizedHighlights, { shouldValidate: true });
    }
  }, [exhibitionEnv, highlightCategory, setValue]);

  useEffect(() => {
    const allowedLabels = [
      'Shopping Center Norte',
      'Lar Center',
      'Shopping Center Norte, Lar Center',
    ];

    setShowToLocations(
      (locations || []).filter((option: { label: string }) => allowedLabels.includes(option.label))
    );
  }, [locations]);

  useEffect(() => {
    const buildCurrentRedeem = (details: any) => {
      if (details?.currentRedeemData) {
        return details.currentRedeemData;
      }

      if (!details?.redeem) {
        return null;
      }

      const partnerId = details?.partner;
      const partnerName = details?.partnerName || '';
      const redeemName = details?.redeemName || '';

      return {
        _id: details.redeem,
        value: details.redeem,
        label: redeemName,
        name: redeemName,
        details: {
          name: redeemName,
        },
        ...(partnerId
          ? {
              partnerId: {
                _id: partnerId,
                name: partnerName,
              },
              partner: {
                _id: partnerId,
                name: partnerName,
              },
            }
          : {}),
      };
    };

    const loadBenefit = async () => {
      if (!benefitId || !benefitTypeParam) {
        setIsDetailsLoading(false);
        return;
      }

      setIsDetailsLoading(true);

      try {
        if (benefitTypeParam === 'coupon') {
          const found = await getCouponDetails(benefitId);
          const details = found?.coupon;

          if (!details) {
            return;
          }

          const codesArray = found?.codes;
          const cpfsArray = details?.cpfs;

          setValue('benefitType', 'coupon');
          setValue('image', details?.image || null);
          setValue('name', details?.name || '');
          setValue('description', details?.description || '');
          setValue('secondaryDescription', details?.secondaryDescription || '');
          setValue('category', details?.category || '');
          setValue('theme', details?.theme || '');
          setValue('rules', details?.rules || '');
          let couponHighlightCategory: string[] = [];
          if (Array.isArray(details?.highlightCategory)) {
            couponHighlightCategory = details.highlightCategory;
          } else if (details?.highlightCategory) {
            couponHighlightCategory = [details.highlightCategory];
          }
          setValue('highlightCategory', couponHighlightCategory);
          setValue('startDate', details?.startDate ? new Date(details.startDate) : new Date());
          setValue('endDate', details?.endDate ? new Date(details.endDate) : new Date());
          setValue('price', details?.price || 0);
          setValue('discountPercentage', details?.discountPercentage || 0);
          setValue('showTo', details?.showTo || []);
          setValue('rewardType', details?.rewardType || 'code');
          setValue('totalQuantity', details?.totalQuantity || 0);
          setValue('expirationDays', details?.expiration?.expirationDays ?? 0);
          setValue('localType', details?.entity?.type || 'company');
          setValue(
            'tiers',
            details?.visibleForTiers?.length === 0 ? ['all'] : details?.visibleForTiers || []
          );
          setValue('exhibitionEnv', details?.exhibitionEnv || 'viva');
          setValue('codeShowType', details?.codeShowType || 'code');
          setValue('priority', details?.priority || 0);
          setValue('private', details?.private || false);
          setValue('dontShowToImported', details?.dontShowToImported || false);
          setValue('blocking', details?.blocking?.type || 'self');
          setValue('userPurchaseLimit', details?.userPurchaseLimit?.limit || 1);
          setValue('test', details?.test || false);
          setValue('redeemCodeQuantity', details?.redeemCodeQuantity || 1);
          setValue('businessUnitId', details?.lpEventsData?.businessUnitId || '');
          setValue('lpEvent', details?.lpEventsData?.eventId || '');
          setValue('exclusiveToBiome', details?.exclusiveToBiome || false);
          setValue('hasQuantity', details?.hasQuantity || false);
          setValue('uniqueCode', details?.uniqueCode || '');
          setValue('onlyToRedeemedPoints', details?.onlyToRedeemedPoints || false);
          setValue('benefitForNewUsers', details?.benefitForNewUsers || false);
          setValue('newUsersPeriod', {
            startDate: details?.newUsersPeriod?.startDate
              ? new Date(details.newUsersPeriod.startDate)
              : new Date(),
            endDate: details?.newUsersPeriod?.endDate
              ? new Date(details.newUsersPeriod.endDate)
              : new Date(),
          });
          setValue('hasCustomerVoice', !!details?.customerVoiceData);
          setValue('customerVoiceData', {
            baseUrl: details?.customerVoiceData?.baseUrl || '',
            assetsUrl: details?.customerVoiceData?.assetsUrl || '',
            customerVoiceFormId: details?.customerVoiceData?.customerVoiceFormId || '',
          });
          setValue('status', details?.status || 'active');
          setCurrentRedeem(buildCurrentRedeem(details));

          if (details?.entity?.type === 'company') {
            setValue('company', {
              value: details?.entity?.entityId,
              label: details?.entity?.entityName,
            });
            setValue('local', null);
          } else {
            setValue('local', {
              value: details?.entity?.entityId,
              label: details?.entity?.entityName,
            });
            setValue('company', null);
          }

          setCpfs(cpfsArray?.length ? cpfsArray.map((cpf: string) => [cpf]) : []);

          if (isCopyMode) {
            setCodes([]);
          } else {
            setCodes(codesArray?.length ? codesArray.map((code: string) => String(code)) : []);
          }

          return;
        }

        const productFound = await findProductById(benefitId, setValue);
        const found = productFound?.product;

        if (!found) {
          return;
        }

        const codesArray = productFound?.codes;
        const cpfsArray = found?.cpfs;

        setValue('benefitType', 'product');
        setValue('image', found?.banner || null);
        setValue('name', found?.name || '');
        setValue('location', found?.local || '');
        setValue('category', found?.category || '');
        setValue('theme', found?.theme || '');
        setValue('priority', found?.priority || 0);
        let productHighlightCategory: string[] = [];
        if (Array.isArray(found?.highlightCategory)) {
          productHighlightCategory = found.highlightCategory;
        } else if (found?.highlightCategory) {
          productHighlightCategory = [found.highlightCategory];
        }
        setValue('highlightCategory', productHighlightCategory);
        setValue('blocking', found?.blocking?.type || 'self');
        setValue('status', found?.status || 'active');
        setValue('price', found?.price || 0);
        setValue('show', found?.toShow || []);
        setValue('startDate', found?.startDate ? new Date(found.startDate) : new Date());
        setValue('endDate', found?.endDate ? new Date(found.endDate) : new Date());
        setValue('rewardType', found?.rewardType || 'simple');
        setValue('totalQuantity', found?.totalQuantity || 0);
        setValue('localType', found?.entity?.type || 'company');
        setValue(
          'tiers',
          found?.visibleForTiers?.length === 0 ? ['all'] : found?.visibleForTiers || []
        );
        setValue('expirationDays', found?.expiration?.expirationDays ?? 0);
        setValue('private', found?.private || false);
        setValue('dontShowToImported', found?.dontShowToImported || false);
        setValue('exhibitionEnv', found?.exhibitionEnv || 'viva');
        setValue('codeShowType', found?.codeShowType || 'code');
        setValue('userPurchaseLimit', found?.userPurchaseLimit?.limit || 1);
        setValue('test', found?.test || false);
        setValue('bonusProduct', found?.bonusProduct || false);
        setValue('isGift', found?.isGift || false);
        setValue('exclusiveToBiome', found?.exclusiveToBiome || false);
        setValue('rules', found?.rules || '');
        setValue('details', found?.details?.map((detail: string) => ({ value: detail })) || []);
        setValue('description', found?.description || '');
        setValue('redeemCodeQuantity', found?.redeemCodeQuantity || 1);
        setValue('businessUnitId', found?.lpEventsData?.businessUnitId || '');
        setValue('lpEvent', found?.lpEventsData?.eventId || '');
        setValue('hasQuantity', found?.hasQuantity || false);
        setValue('uniqueCode', found?.uniqueCode || '');
        setValue('onlyToRedeemedPoints', found?.onlyToRedeemedPoints || false);
        setValue('benefitForNewUsers', found?.benefitForNewUsers || false);
        setValue('newUsersPeriod', {
          startDate: found?.newUsersPeriod?.startDate
            ? new Date(found.newUsersPeriod.startDate)
            : new Date(),
          endDate: found?.newUsersPeriod?.endDate
            ? new Date(found.newUsersPeriod.endDate)
            : new Date(),
        });
        setValue('hasCustomerVoice', !!found?.customerVoiceData);
        setValue('customerVoiceData', {
          baseUrl: found?.customerVoiceData?.baseUrl || '',
          assetsUrl: found?.customerVoiceData?.assetsUrl || '',
          customerVoiceFormId: found?.customerVoiceData?.customerVoiceFormId || '',
        });
        setCurrentRedeem(buildCurrentRedeem(found));

        if (found?.entity?.type === 'company') {
          setValue('company', {
            value: found?.entity?.entityId,
            label: found?.entity?.entityName,
          });
          setValue('local', null);
        } else {
          setValue('local', {
            value: found?.entity?.entityId,
            label: found?.entity?.entityName,
          });
          setValue('company', null);
        }

        setCpfs(cpfsArray?.length ? cpfsArray.map((cpf: string) => [cpf]) : []);
        setCodes(codesArray?.length ? codesArray.map((code: string) => String(code)) : []);
      } catch (error) {
        console.error(error);
        enqueueSnackbar('Erro ao carregar benefício', { variant: 'error' });
      } finally {
        setIsDetailsLoading(false);
      }
    };

    if (!shouldLoadBenefit) {
      setIsDetailsLoading(false);
      return;
    }

    loadBenefit();
  }, [
    benefitId,
    benefitTypeParam,
    getCouponDetails,
    findProductById,
    setValue,
    enqueueSnackbar,
    isCopyMode,
    shouldLoadBenefit,
  ]);

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      setValue(
        'image',
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
        { shouldValidate: true }
      );
    },
    [setValue]
  );

  const handleUploadCpfs = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();

      reader.onload = (loadEvent) => {
        const data = loadEvent.target?.result;
        const readedData = XLSX.read(data, { type: 'binary' });
        const wsname = readedData.SheetNames[0];
        const ws = readedData.Sheets[wsname];
        const dataParse: any = XLSX.utils.sheet_to_json(ws, { header: 1 });

        if (dataParse?.[0]?.[0] !== 'Cpfs abaixo') {
          enqueueSnackbar('Modelo de planilha inválido!', { variant: 'error' });
          return;
        }

        const updatedDataParse = dataParse?.slice(1);
        setFileNameCpfs(file.name);
        setCpfs(updatedDataParse);
        enqueueSnackbar('CPFs adicionados com Sucesso!', { variant: 'success' });
        setOpenCpfs(false);
      };

      reader.readAsBinaryString(file);
    },
    [enqueueSnackbar]
  );

  const handleDownloadCpfs = useCallback(() => {
    const cpfsToDownload = cpfs
      .map((cpf) => (Array.isArray(cpf) ? cpf?.[0] : cpf))
      .filter(Boolean)
      .map((cpf) => String(cpf));

    if (!cpfsToDownload.length) {
      return;
    }

    const worksheet = XLSX.utils.aoa_to_sheet([
      ['Cpfs abaixo'],
      ...cpfsToDownload.map((cpf) => [cpf]),
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cpfs');

    XLSX.writeFile(workbook, `cpfs_beneficio_${benefitId || new Date().getTime()}.xlsx`);
  }, [benefitId, cpfs]);

  const handleUpload = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setCodesEdited(true);
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();

      reader.onload = (loadEvent) => {
        const data = loadEvent.target?.result;
        const readedData = XLSX.read(data, { type: 'binary' });
        const wsname = readedData.SheetNames[0];
        const ws = readedData.Sheets[wsname];
        const dataParse: any = XLSX.utils.sheet_to_json(ws, { header: 1 });

        if (dataParse?.[0]?.[0] !== 'Códigos abaixo') {
          enqueueSnackbar('Modelo de planilha inválido!', { variant: 'error' });
          return;
        }

        const parsedCodes =
          dataParse
            ?.slice(1)
            ?.filter((row: any) => row?.[0])
            ?.map((row: any) => String(row[0])) || [];

        setFileName(file.name);
        setCodes(parsedCodes);
        enqueueSnackbar('Códigos adicionados com Sucesso!', { variant: 'success' });
        setOpen(false);
      };

      reader.readAsBinaryString(file);
    },
    [enqueueSnackbar]
  );

  const handleDownloadCodes = useCallback(() => {
    const codesToDownload = codes.filter(Boolean).map((code) => String(code));

    if (!codesToDownload.length) {
      return;
    }

    const worksheet = XLSX.utils.aoa_to_sheet([
      ['Códigos abaixo'],
      ...codesToDownload.map((code) => [code]),
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Códigos');

    XLSX.writeFile(workbook, `codigos_beneficio_${benefitId || new Date().getTime()}.xlsx`);
  }, [benefitId, codes]);

  const onCloseCpfs = useCallback(() => {
    setOpenCpfs(false);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const onCloseRedeemModal = useCallback(() => {
    setOpenRedeemModal(false);
  }, []);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'details',
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      let entityId = null;
      let entityName = null;

      if (data.localType === 'company') {
        entityId = data.company?.value || null;
        entityName = data.company?.label || null;
      } else if (data.localType === 'local') {
        entityId = data.local?.value || null;
        entityName = data.local?.label || null;
      }

      if (data.benefitType === 'coupon' && data.exhibitionEnv !== 'whatsapp') {
        const shouldValidateCouponCodes =
          data.exhibitionEnv !== 'parking' && data.rewardType === 'code';

        if (shouldValidateCouponCodes && codes.length === 0) {
          enqueueSnackbar('Adicione códigos para continuar', { variant: 'error' });
          return;
        }

        if (
          (!isEdit && shouldValidateCouponCodes && codes.length % data.redeemCodeQuantity !== 0) ||
          (isEdit &&
            codesEdited &&
            shouldValidateCouponCodes &&
            codes.length % data.redeemCodeQuantity !== 0)
        ) {
          enqueueSnackbar(
            'Quantidade de códigos/links deve ser múltiplo da quantidade de códigos/links por resgate',
            { variant: 'error' }
          );
          return;
        }

        let couponCodes: string[];

        if (isEdit) {
          couponCodes = codesEdited ? codes : [];
        } else {
          couponCodes = codes;
        }

        const couponPayload: any = {
          _id: benefitId,
          image: data.image || null,
          name: data.name,
          description: data.description,
          secondaryDescription: data.secondaryDescription,
          category: data.category || '',
          theme: data.theme || '',
          rules: data.rules,
          startDate: data.startDate,
          endDate: data.endDate,
          price: data.price,
          ...(data.exhibitionEnv === 'parking'
            ? {
                discountPercentage: data.discountPercentage,
              }
            : {}),
          showTo: data.showTo,
          totalQuantity: data.totalQuantity,
          ...(couponCodes !== undefined ? { codes: couponCodes || [] } : {}),
          cpfs: cpfs.map((c) => c?.[0]) || [],
          rewardType: data.rewardType,
          expiration: {
            expirationType: 'days',
            expirationDays: data.expirationDays,
          },
          localType: data.localType,
          location: data.local,
          store: null,
          status: data.status,
          entity: {
            type: data.localType,
            entityId,
            entityName,
          },
          exhibitionEnv: data.exhibitionEnv,
          visibleForTiers:
            Array.isArray(data.tiers) && data.tiers.length === 1 && data.tiers[0] === 'all'
              ? []
              : data.tiers,
          priority: data.priority,
          partner: currentRedeem?.partnerId?._id,
          partnerName: currentRedeem?.partnerId?.name,
          redeem: currentRedeem?._id,
          redeemName: currentRedeem?.label || currentRedeem?.details?.name || currentRedeem?.name,
          currentRedeemData: currentRedeem || {},
          giftbackOptions: [],
          ...(data.exhibitionEnv === 'lp_events'
            ? {
                highlightCategory: data.highlightCategory.filter(
                  (item) => item === 'lp_events_home_highlights' || item === 'without'
                ),
              }
            : {}),
          private: data.private,
          test: data.test,
          dontShowToImported: data.dontShowToImported,
          blocking: {
            type: data.blocking,
          },
          userPurchaseLimit: {
            limit: data.userPurchaseLimit,
          },
          codeShowType: data.codeShowType,
          redeemCodeQuantity: data.rewardType === 'code' ? data.redeemCodeQuantity : 1,
          lpEventsData: {
            businessUnitId: data.businessUnitId || null,
            eventId: data.lpEvent || null,
          },
          exclusiveToBiome: data.exclusiveToBiome,
          hasQuantity: data.hasQuantity || false,
          uniqueCode: data.uniqueCode || '',
          onlyToRedeemedPoints: data.onlyToRedeemedPoints || false,
          benefitForNewUsers: data.benefitForNewUsers,
          newUsersPeriod: data.newUsersPeriod,
          customerVoiceData: data.hasCustomerVoice
            ? {
                baseUrl: data.customerVoiceData.baseUrl || null,
                assetsUrl: data.customerVoiceData.assetsUrl || null,
                customerVoiceFormId: data.customerVoiceData.customerVoiceFormId || null,
              }
            : null,
        };

        if (isEdit && benefitId) {
          await updateCoupon(couponPayload);
          enqueueSnackbar('Cupom atualizado com sucesso!');
        } else {
          delete couponPayload._id;
          await createCoupon(couponPayload);
          enqueueSnackbar('Cupom criado com sucesso!');
        }

        navigate(paths.dashboard.benefitsList);
        return;
      }

      const shouldValidateDisplayEntity = data.exhibitionEnv !== 'whatsapp';

      if (shouldValidateDisplayEntity && data.localType === 'local' && !data.local) {
        enqueueSnackbar('Local é obrigatório', { variant: 'error' });
        return;
      }

      if (shouldValidateDisplayEntity && data.localType === 'company' && !data.company) {
        enqueueSnackbar('Empresa é obrigatória', { variant: 'error' });
        return;
      }

      let bannerUrl = '';

      if (data.image) {
        const [image, format] = await convertFile(data.image);

        if (format === 'error') {
          enqueueSnackbar('Ocorreu um Erro', { variant: 'error' });
          return;
        }

        if (format === 'base64') {
          const uploaded = await uploadImage(image);
          if (uploaded === 'error') {
            enqueueSnackbar('Erro ao fazer upload da imagem', { variant: 'error' });
            return;
          }
          bannerUrl = uploaded;
        }

        if (format === 'url') {
          bannerUrl = image;
        }
      }

      if (data.localType === 'company') {
        const foundByName = companies.find((company: any) => company.name === data?.company?.label);
        entityName = data.company?.label || (data.company as any) || null;
        entityId = data.company?.value || (foundByName as any)?._id || null;
      } else if (data.localType === 'local') {
        entityId = data.local?.value || null;
        entityName = data.local?.label || null;
      }

      const productPayload: any = {
        _id: benefitId,
        name: data.name,
        description: data.description,
        category: data.category,
        theme: data.theme || null,
        ...(data.exhibitionEnv === 'lp_events'
          ? {
              highlightCategory: data.highlightCategory.filter(
                (item) => item === 'lp_events_home_highlights' || item === 'without'
              ),
            }
          : {}),
        store: null,
        local: data.location,
        banner: bannerUrl || null,
        status: data.status,
        price: data.price,
        rules: data.rules,
        toShow: data.show,
        startDate: data.startDate,
        endDate: data.endDate,
        rewardType: data.rewardType,
        totalQuantity: data.totalQuantity,
        expiration: {
          expirationType: 'days',
          expirationDays: data.expirationDays,
        },
        codes: [],
        cpfs: cpfs.map((c) => c?.[0]) || [],
        partner: currentRedeem?.partnerId?._id,
        partnerName: currentRedeem?.partnerId?.name,
        redeem: currentRedeem?._id,
        redeemName: currentRedeem?.label || currentRedeem?.details?.name || currentRedeem?.name,
        currentRedeemData: currentRedeem || {},
        visibleForTiers:
          Array.isArray(data.tiers) && data.tiers.length === 1 && data.tiers[0] === 'all'
            ? []
            : data.tiers,
        priority: data.priority,
        details: data.details.map((detail: any) => detail.value),
        entity: {
          type: data.localType,
          entityId,
          entityName,
        },
        exhibitionEnv: data.exhibitionEnv,
        private: data.private,
        dontShowToImported: data.dontShowToImported,
        blocking: {
          type: data.blocking,
        },
        userPurchaseLimit: {
          limit: data.userPurchaseLimit,
        },
        isGift: data.isGift,
        codeShowType: data.codeShowType,
        test: data.test,
        bonusProduct: data.bonusProduct,
        redeemCodeQuantity: data.rewardType === 'code' ? data.redeemCodeQuantity : 1,
        news: false,
        tag: '',
        exclusiveToBiome: data.exclusiveToBiome,
        lpEventsData: {
          businessUnitId: data.businessUnitId || null,
          eventId: data.lpEvent || null,
        },
        hasQuantity: data.hasQuantity || false,
        uniqueCode: data.uniqueCode || '',
        onlyToRedeemedPoints: data.onlyToRedeemedPoints || false,
        benefitForNewUsers: data.benefitForNewUsers || false,
        newUsersPeriod: data.newUsersPeriod || {},
        customerVoiceData: data.hasCustomerVoice
          ? {
              baseUrl: data.customerVoiceData.baseUrl || null,
              assetsUrl: data.customerVoiceData.assetsUrl || null,
              customerVoiceFormId: data.customerVoiceData.customerVoiceFormId || null,
            }
          : null,
      };

      if (isEdit && benefitId) {
        const updated = await updateProduct(productPayload);
        if (!updated) {
          enqueueSnackbar('Erro ao atualizar produto', { variant: 'error' });
          return;
        }
        enqueueSnackbar('Produto atualizado com sucesso', { variant: 'success' });
      } else {
        delete productPayload._id;
        const created = await createProduct(productPayload);
        if (!created) {
          enqueueSnackbar('Erro ao criar produto', { variant: 'error' });
          return;
        }
        enqueueSnackbar('Produto criado com sucesso', { variant: 'success' });
      }

      navigate(paths.dashboard.benefitsList);
    } catch (error) {
      enqueueSnackbar('Ocorreu um erro ao salvar o benefício', { variant: 'error' });
      console.error(error);
    }
  });

  useEffect(() => {
    if (Object.keys(formState.errors).length > 0) {
      console.log('Form errors:', formState.errors);
    }
  }, [formState.errors]);

  return {
    title,
    isEdit,
    methods,
    control,
    open,
    part,
    allredeems,
    currentRedeem,
    formState,
    fileName,
    codes,
    codesEdited,
    openCpfs,
    openRedeemModal,
    fileNameCpfs,
    cpfs,
    fields,
    showToLocations,
    isDetailsLoading,
    CATEGORY_OPTIONS: categories,
    THEME_OPTIONS: themes,
    LOCATIONS_OPTIONS: locations,
    COMPANY_OPTIONS: companies.map((company: any) => ({
      value: company._id,
      label: company.name,
      image: company.image || null,
    })),
    BUSINESS_UNIT_OPTIONS: businessUnits,
    LP_EVENTS_OPTIONS: lpEventsByBusinessUnit,
    EXHIBITION_ENV_OPTIONS,
    BOOLEAN_OPTIONS,
    BOOLEAN_LABELS,
    PRIVATE_OPTIONS,
    PRIVATE_LABELS,
    DONT_OPTIONS,
    DONT_LABELS,
    BLOCK_REDEEM_OPTIONS,
    BLOCK_REDEEM_LABELS,
    TIER_OPTIONS,
    BENEFIT_TYPE_OPTIONS,
    BENEFIT_TYPE_LABELS,
    LOCAL_TYPE_OPTIONS,
    LOCAL_TYPE_LABELS,
    COUPON_REWARD_OPTIONS,
    COUPON_REWARD_LABELS,
    PRODUCT_REWARD_OPTIONS,
    PRODUCT_REWARD_LABELS,
    PRODUCT_WPP_REWARD_OPTIONS,
    PRODUCT_WPP_REWARD_LABELS,
    CODE_SHOW_OPTIONS,
    CODE_SHOW_LABELS,
    watch,
    onSubmit,
    handleDrop,
    setOpen,
    setCurrentRedeem,
    setOpenRedeemModal,
    onClose,
    onCloseRedeemModal,
    handleUpload,
    handleDownloadCodes,
    setOpenCpfs,
    onCloseCpfs,
    handleUploadCpfs,
    handleDownloadCpfs,
    appendDetail: () => append({ value: '' }),
    removeDetail: remove,
  };
};
